import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CircleAlert,
  LayoutTemplate,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react';

import {
  useTemplates2AssignDefault,
  useTemplates2FindOne,
  useTemplates2GetDefaults,
  useTemplates2GetPotentialDefaults,
} from '@/lib/api/requests/templates.requests';
import { cn } from '@/lib/utils/cn';
import { Badge } from '@/ui-components/ui/badge';
import { Button } from '@/ui-components/ui/button';
import { Card, CardContent, CardHeader } from '@/ui-components/ui/card';
import { Dialog, DialogContent } from '@/ui-components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/util-components/dropdown-menu';
import { Input } from '@/ui-components/ui/input';
import { Skeleton } from '@/ui-components/ui/skeleton';
import {
  DataView,
  DataViewContent,
  DataViewEmptyState,
  DataViewGridCard,
  DataViewLoadingState,
  DataViewToolbar,
  GenericItem,
} from '@/util-components/data-view';
import { StandardDialogHeader } from '@/util-components/dialog/components/StandardDialogHeader';
import { PageInfoBanner } from '@/util-components/page-layout/page-info-banner';
import { SubPageLayout } from '@/util-components/page-layout/sub-page-layout';

import { DefaultTemplateConfig, DefaultTemplateType } from '../template-editor/registry/defaultTemplates';

type DefaultTemplateCardConfig = (typeof DefaultTemplateConfig)[DefaultTemplateType];
type SelectionModalState = {
  type: DefaultTemplateType;
  entityType: string;
  label: string;
} | null;

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Match: 'Spiel',
  Player: 'Spieler',
};

const DEFAULT_TEMPLATE_ENTRIES = Object.entries(DefaultTemplateConfig) as Array<
  [DefaultTemplateType, DefaultTemplateCardConfig]
>;

const getEntityTypeLabel = (entityType: string) => ENTITY_TYPE_LABELS[entityType] ?? entityType;

const DefaultTemplatesSkeleton = () => (
  <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
    <div className="space-y-8">
      <div className="flex items-start gap-4">
        <Skeleton className="h-12 w-12 rounded-2xl" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-7 w-52 max-w-full" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
      </div>

      <Card className="overflow-hidden border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gray-50/30 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <Skeleton className="h-10 w-full max-w-md rounded-xl" />
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <Skeleton className="h-6 w-32 rounded-full" />
            </div>
          </div>
        </CardHeader>

        <CardContent className="bg-white p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-2xl border border-gray-200 p-4 sm:p-5">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-10 w-10 rounded-xl" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-5 w-32 max-w-full" />
                    <Skeleton className="h-4 w-20 rounded-full" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <Skeleton className="h-4 w-28 max-w-full" />
                      <Skeleton className="h-3 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-8 w-8 rounded-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const DefaultTemplatesPage = () => {
  const navigate = useNavigate();
  const { highlightType } = useParams<{ highlightType?: string }>();

  const { data: defaults, isLoading: isLoadingDefaults, refetch } = useTemplates2GetDefaults({ enabled: true });
  const { mutateAsync: assignDefault, isPending: isAssigningDefault } = useTemplates2AssignDefault();

  const [selectionModal, setSelectionModal] = useState<SelectionModalState>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const activeHighlightType: DefaultTemplateType | undefined =
    (Object.values(DefaultTemplateType) as string[]).includes(highlightType || '')
      ? (highlightType as DefaultTemplateType)
      : undefined;

  const highlightRef = useRef<HTMLDivElement>(null);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  useEffect(() => {
    if (activeHighlightType && !isLoadingDefaults) {
      const timer = setTimeout(() => {
        highlightRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [activeHighlightType, isLoadingDefaults]);

  const assignedCount = useMemo(
    () => DEFAULT_TEMPLATE_ENTRIES.filter(([type]) => Boolean(defaults?.[type])).length,
    [defaults],
  );

  const missingCount = DEFAULT_TEMPLATE_ENTRIES.length - assignedCount;
  const hasUnassigned = !isLoadingDefaults && missingCount > 0;
  const assignmentStatusBadgeClassName =
    assignedCount === 0
      ? 'border-red-200 bg-red-50 text-red-700'
      : assignedCount === DEFAULT_TEMPLATE_ENTRIES.length
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : 'border-amber-200 bg-amber-50 text-amber-700';

  const filteredTemplateEntries = useMemo(() => {
    if (!normalizedSearchQuery) {
      return DEFAULT_TEMPLATE_ENTRIES;
    }

    return DEFAULT_TEMPLATE_ENTRIES.filter(([, config]) =>
      [config.label, config.description, getEntityTypeLabel(config.entityType)]
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearchQuery),
    );
  }, [normalizedSearchQuery]);

  const handleAssign = async (templateId: string) => {
    if (!selectionModal) return;

    try {
      await assignDefault({
        templateType: selectionModal.type,
        templateId,
      });

      setSelectionModal(null);
      await refetch();
    } catch (error) {
      console.error('Failed to assign template', error);
    }
  };

  const handleUnassign = async (type: DefaultTemplateType) => {
    try {
      await assignDefault({
        templateType: type,
        templateId: '',
      });

      await refetch();
    } catch (error) {
      console.error('Failed to unassign template', error);
    }
  };

  if (isLoadingDefaults) {
    return (
      <SubPageLayout
        pageTitle="Standard-Templates"
        icon={<LayoutTemplate />}
        navigateBackCallback={() => navigate(-1)}
      >
        <DefaultTemplatesSkeleton />
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      pageTitle="Standard-Templates"
      icon={<LayoutTemplate />}
      navigateBackCallback={() => navigate(-1)}
    >
      <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6">
        <div className="space-y-8">
          {hasUnassigned && (
            <PageInfoBanner
              icon={CircleAlert}
              title="Einrichtung unvollständig"
              description="Es sind noch nicht allen Kategorien Standard-Templates zugewiesen. Für eine optimale Automatisierung empfehlen wir, alle Kategorien zu belegen."
              variant="blue"
            />
          )}

          <Card className="overflow-hidden border-gray-200 shadow-sm">
            <CardHeader className="border-b border-gray-100 bg-gray-50/30 px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full max-w-md">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Kategorien durchsuchen..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="h-10 rounded-xl border-gray-200 bg-white pl-9 pr-9 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  />
                  {searchQuery && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Suche zurücksetzen</span>
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                  <Badge
                    variant="outline"
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                      assignmentStatusBadgeClassName,
                    )}
                  >
                    {assignedCount} von {DEFAULT_TEMPLATE_ENTRIES.length} zugewiesen
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="bg-white p-4 sm:p-6">
              {filteredTemplateEntries.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  {filteredTemplateEntries.map(([type, config]) => (
                    <DefaultTemplateCard
                      key={type}
                      config={config}
                      assignedTemplateId={defaults?.[type]}
                      isHighlighted={activeHighlightType === type}
                      cardRef={activeHighlightType === type ? highlightRef : undefined}
                      isBusy={isAssigningDefault}
                      onSelect={() =>
                        setSelectionModal({
                          type,
                          entityType: config.entityType,
                          label: config.label,
                        })
                      }
                      onRemove={() => handleUnassign(type)}
                    />
                  ))}
                </div>
              ) : (
                <DataViewEmptyState
                  icon={<Search className="h-7 w-7 text-gray-300" />}
                  title="Keine Kategorien gefunden"
                  description={`Für "${searchQuery}" gibt es aktuell keine passende Kategorie.`}
                  action={
                    <Button variant="outline" onClick={() => setSearchQuery('')} className="h-10 rounded-xl px-4 text-sm font-semibold">
                      Suche zurücksetzen
                    </Button>
                  }
                  className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 py-12"
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <TemplateSelectionDialog
        open={Boolean(selectionModal)}
        onOpenChange={(open) => !open && setSelectionModal(null)}
        entityType={selectionModal?.entityType || ''}
        categoryLabel={selectionModal?.label || ''}
        isSubmitting={isAssigningDefault}
        onSelect={handleAssign}
      />
    </SubPageLayout>
  );
};

const DefaultTemplateCard = ({
  config,
  assignedTemplateId,
  isHighlighted,
  cardRef,
  isBusy,
  onSelect,
  onRemove,
}: {
  config: DefaultTemplateCardConfig;
  assignedTemplateId?: string;
  isHighlighted?: boolean;
  cardRef?: RefObject<HTMLDivElement>;
  isBusy: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) => {
  const { data: template, isLoading } = useTemplates2FindOne(assignedTemplateId || '', {
    enabled: Boolean(assignedTemplateId),
  });
  const IconComponent = config.icon || LayoutTemplate;
  const hasAssignedTemplate = Boolean(assignedTemplateId);

  return (
    <div
      ref={cardRef}
      className={cn(
        'group rounded-2xl border bg-white p-4 shadow-sm transition-all duration-200 sm:p-5',
        isHighlighted
          ? 'border-blue-200 ring-2 ring-blue-100 shadow-blue-100/60'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md',
      )}
    >
      <div className="flex h-full flex-col gap-4">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors',
              hasAssignedTemplate
                ? 'border-blue-100 bg-blue-50 text-blue-600'
                : 'border-gray-100 bg-gray-50 text-gray-400',
            )}
          >
            <IconComponent className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1 space-y-1.5">
            <h3 className="text-sm font-semibold tracking-tight text-gray-900">{config.label}</h3>
            <p className="text-xs leading-relaxed text-gray-500">{config.description}</p>
          </div>
        </div>

        <div className="mt-auto">
          {hasAssignedTemplate ? (
            <div className="-mx-1 rounded-xl bg-gray-50/60 px-2 py-2 sm:mx-0 sm:border sm:border-gray-100 sm:bg-gray-50/50 sm:p-3">
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white shadow-sm">
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-300" />
                    ) : template?.meta?.thumbnailPath ? (
                      <img src={template.meta.thumbnailPath} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <LayoutTemplate className="h-4 w-4 text-gray-300" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-gray-900">
                      {isLoading ? 'Template wird geladen...' : template?.title || 'Unbekannt'}
                    </p>
                    <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">Aktuell als Standard hinterlegt</p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center justify-end self-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isBusy}
                        className="h-8 w-8 rounded-lg border border-transparent text-gray-400 hover:border-gray-200 hover:bg-white hover:text-gray-900"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Aktionen</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 rounded-xl">
                      <DropdownMenuItem onClick={onSelect} className="cursor-pointer py-2 text-xs font-medium">
                        <RefreshCw className="mr-2 h-3.5 w-3.5 text-blue-500" />
                        Template ändern
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={onRemove}
                        className="cursor-pointer py-2 text-xs font-medium text-red-600 focus:bg-red-50 focus:text-red-600"
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Zuweisung aufheben
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onSelect}
              disabled={isBusy}
              className="h-10 w-full rounded-xl border-dashed border-gray-200 bg-gray-50 text-xs font-semibold text-gray-600 transition-all hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Template auswählen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplateSelectionDialog = ({
  open,
  onOpenChange,
  entityType,
  categoryLabel,
  isSubmitting,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: string;
  categoryLabel: string;
  isSubmitting: boolean;
  onSelect: (id: string) => void;
}) => {
  const { data: templates, isLoading } = useTemplates2GetPotentialDefaults(entityType, { enabled: open });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open, entityType]);

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!templates) {
      return [];
    }

    if (!normalizedQuery) {
      return templates;
    }

    return templates.filter((template) => template.title.toLowerCase().includes(normalizedQuery));
  }, [searchQuery, templates]);

  const genericItems: GenericItem[] = useMemo(
    () =>
      filteredTemplates.map((template) => ({
        id: template.id,
        title: template.title,
        description: 'Als Standard verwenden',
        previewContent: (
          <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-slate-100 via-white to-slate-100 p-2 transition-transform duration-500 group-hover:scale-[1.02]">
            {template.meta?.thumbnailPath ? (
              <img
                src={template.meta.thumbnailPath}
                alt=""
                className="max-h-full max-w-full rounded-sm object-contain drop-shadow-md"
                loading="lazy"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-300">
                <LayoutTemplate className="h-10 w-10 opacity-25" />
              </div>
            )}
          </div>
        ),
      })),
    [filteredTemplates],
  );

  const resultLabel = `${genericItems.length} ${genericItems.length === 1 ? 'Template' : 'Templates'}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => event.preventDefault()}
        className="left-0 top-0 flex h-[100vh] w-screen max-h-none max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden border-0 bg-white p-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-[max(0.5rem,env(safe-area-inset-top))] shadow-2xl sm:left-[50%] sm:top-[50%] sm:h-[85vh] sm:max-h-[90vh] sm:w-full sm:max-w-6xl sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-[32px] sm:pb-0 sm:pt-0"
      >
        <div className="shrink-0 border-b border-gray-100 bg-white">
          <div className="px-5 py-4 sm:px-6 sm:py-5">
            <StandardDialogHeader
              icon={LayoutTemplate}
              title={categoryLabel ? `Template für ${categoryLabel} wählen` : 'Template auswählen'}
              description={`Wähle ein passendes Template für ${getEntityTypeLabel(entityType)}-Posts aus.`}
              className="border-none p-0 pb-0"
            />
          </div>
        </div>

        <DataView
          viewMode="grid"
          isLoading={isLoading}
          isEmpty={!isLoading && genericItems.length === 0}
          className="flex min-h-0 flex-1 flex-col bg-gray-50/80"
        >
          <DataViewToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Templates durchsuchen..."
            hideViewModeToggle
            searchDebounceMs={150}
            infoText={!isLoading ? resultLabel : 'Lädt...'}
            trailing={
              searchQuery ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="h-9 rounded-xl px-3 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                >
                  <X className="mr-2 h-4 w-4" />
                  Zurücksetzen
                </Button>
              ) : null
            }
          />

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
              <DataViewContent
                items={genericItems}
                className="min-h-0 space-y-4"
                loadingIndicator={<DataViewLoadingState itemCount={8} className="gap-4 sm:gap-5" />}
                emptyState={
                  <DataViewEmptyState
                    icon={<Search className="h-7 w-7 text-gray-300" />}
                    title="Keine Templates gefunden"
                    description={
                      searchQuery
                        ? `Für "${searchQuery}" konnten wir keine Templates finden.`
                        : 'Momentan sind keine passenden Templates für diesen Typ verfügbar.'
                    }
                    action={
                      searchQuery ? (
                        <Button variant="outline" onClick={() => setSearchQuery('')} className="h-10 rounded-xl px-4 text-sm font-semibold">
                          Suche zurücksetzen
                        </Button>
                      ) : undefined
                    }
                    className="rounded-2xl border border-dashed border-gray-200 bg-white py-12"
                  />
                }
                renderGridItem={(item: GenericItem) => (
                  <DataViewGridCard
                    key={item.id}
                    item={item}
                    className={cn('rounded-2xl border-gray-200 shadow-sm', isSubmitting && 'pointer-events-none opacity-70')}
                    actions={{
                      onItemClick: () => onSelect(item.id),
                    }}
                  />
                )}
              />
            </div>
          </div>
        </DataView>
      </DialogContent>
    </Dialog>
  );
};

export default DefaultTemplatesPage;
